import React, { FC, useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';

import { api, Booking } from '../../../logic/api';
import TextInputField from '../../editable/EditableField/fields/TextInputField';

import { SessionContext } from '../../../utility/useSession';
import { useDebounce } from '../../../utility/useDebounce';
import { usePreferences } from "../../../utility/usePreferences";

import { filterUniqueCustomers } from './helpers';
import { customerLookUpStyles, useCustomerLookUp } from './hooks';
import { ResultsList } from './results-list.component';
import { useTranslation } from 'react-i18next';

export const CustomerLookUpDialog: FC = () => {
  const {
    customerLastName,
    setCustomerLastName,
    vehicleRegistration,
    setVehicleRegistration,
    setCustomerLookUpSelection,
    customerLookUpDialogIsOpen,
    setCustomerLookUpDialogIsOpen,
  } = useCustomerLookUp();
  const [results, setResults] = useState<Booking[]>([]);
  const findManyByVehicleOrLeadContact = api.hooks.booking.findManyByVehicleOrLeadContact();
  const session = useContext(SessionContext);
  const registrationSearchTerm = useDebounce(vehicleRegistration, 500);
  const lastNameSearchTerm = useDebounce(customerLastName, 500);
  const { t } = useTranslation();

  const fetchBookings = () => {
    return new Promise<void>((resolve) => {
      findManyByVehicleOrLeadContact.send(session.current.context, {
        registration: registrationSearchTerm,
        lastName: lastNameSearchTerm,
      }, ((response) => {
        if (response && Array.isArray(response)) {
          const uniqueResults = filterUniqueCustomers(response.reverse());

          setResults(uniqueResults);
        }

        resolve()
      }))
    })
  }

  useEffect(() => {
    if (lastNameSearchTerm || registrationSearchTerm) {
      fetchBookings()
    }
  }, [lastNameSearchTerm, registrationSearchTerm])

  const { usesCustomerLookUp } = usePreferences();
  if (!usesCustomerLookUp()) {
    return null;
  }

  const dispose = () => {
    setCustomerLastName('');
    setVehicleRegistration('');
    setResults([]);
  }

  const onClose = () => {
    setCustomerLookUpDialogIsOpen(false);
    dispose();
  }

  return (
    <Dialog
      open={customerLookUpDialogIsOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Typography variant="h6">{t("dialogs.customerSearch")}</Typography>
        <IconButton
          sx={customerLookUpStyles.closeButton}
          aria-label="close"
          onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{
          marginBottom: "12px",
        }}>{t("dialogs.searchPrevBookings")}</DialogContentText>
        <Box sx={customerLookUpStyles.searchWrapper}>
          <TextInputField
            autoFocus
            label={t("booking.labels.lastName")}
            onChange={(value) => setCustomerLastName(value)}
            value={customerLastName}
            maxWidth={180}
            testId="customer-surname-input"
          />
          <DialogContentText style={{ marginBottom: 0 }}>{t("general.andOr")}</DialogContentText>
          <TextInputField
            label={t("dialogs.vehicleRegistration")}
            onChange={(value) => setVehicleRegistration(value)}
            value={vehicleRegistration}
            maxWidth={180}
            testId="vehicle-registration-input"
          />
          <Button
            variant="contained"
            startIcon={<Search />}
            color="primary"
            data-testid="search-button"
            onClick={fetchBookings}
          >
            {t("home.search.searchFieldLabel")}
          </Button>
        </Box>
        <ResultsList
          bookings={results}
          onItemSelected={(booking) => {
            setCustomerLookUpSelection(booking);
            onClose();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
